// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from 'react-native';

import {
  HoleMapCanvas,
  UNNUMBERED_HOLE,
  type DraggedMarker,
  type KnownHoleMarker,
  type MapOverlayLine,
  type MapOverlayPolygon,
} from '@/features/hole/holeview/HoleMapCanvas';
import {
  DRAWING_FILLS,
  OVERLAY_COLORS,
  playingLineFor,
  ringAreaSquareMeters,
} from '@/features/hole/holeview/holeShapes';
import { isNationalOrtho } from '@/features/hole/holeview/tileSource';
import { useCourseStakes } from '@/features/course-stakes/useCourseStakes';
import { parseLatLon } from './coordinateInput';
import { lengthSourceLabel } from './lengthSourceLabel';
import { correctedCoursePosition } from './coursePositionFromGeometry';
import { requestCourseAnalysis, useCourseAnalysisStatus } from './useCourseAnalysisStatus';
import { CANDIDATE_SOURCE, candidateKey, useAnalysisCandidates } from './useAnalysisCandidates';
import {
  EVALUATION_SOURCE,
  FALSE_POSITIVE,
  MATCHED,
  MISSED,
  evaluationKey,
  useEvaluationFeatures,
} from './useEvaluationFeatures';
import {
  IDENTITY_VIEW,
  MAX_EDIT_ZOOM,
  MAX_VIEW_ZOOM,
  MIN_EDIT_ZOOM,
  MIN_VIEW_ZOOM,
  distanceMeters,
  pointInPolygon,
  type LatLon,
  type ViewTransform,
} from '@/features/hole/holeview/tileMath';
import type { TeePoint } from '@/features/hole/holeview/useHoleViewGeometry';
import { formatArea, formatHoleDistance, type DistanceUnit } from '@/features/units/distance';
import { useI18n } from '@/i18n';
import { useDistanceUnit } from '@/stores/distanceUnitState';
import appColors, { alpha } from '@/theme/colors';

import {
  draftFromFeatures,
  isDirty as isDraftDirty,
  markerKey,
  moveMarker,
  placeMarker,
  placeTurnPoint,
  placedTurnPoints,
  removeMarker,
  removedFeatureIds,
  toManualFeatures,
  trimTurnPoints,
  type HoleDraft,
  type MarkerKind,
} from './holeGeometryDraft';
import { holePath, pathLengthMeters, turnPointCount } from './holeTurnPoints';
import {
  framingRingsForHole,
  layersOf,
  shapesForHole,
  toggleKind,
  visibleShapes,
} from './holeShapeLayers';
import { HoleLengthTable } from './HoleLengthTable';
import { matchTeeByLength, nearestTeeByLength, teeLengthChoices } from './teeLengthMatch';
import { useCourseHoles, type CourseHoleTeeLength } from './useCourseHoles';
import { useHoleFeatures, type HoleFeature, type HoleShapeFeature } from './useHoleFeatures';
import { useManualHoleFeatures } from './useManualHoleFeatures';

/** "satellite" alone is ambiguous now that two imagery providers both write rows. */
export const sourceLabel = (source: string, sourceProvider: string | null) =>
  source === 'satellite' && sourceProvider ? `${source} · ${sourceProvider}` : source;

/**
 * Which of `shapes` a tapped point lands on, topmost first — the last drawn, same convention
 * `HoleGuideCard` uses for its own shape-tap hit test.
 *
 * Ray-cast in plain lat/lon rather than a projected space: the ring and the tapped point come
 * from the same `onMapPress` un-projection either way, and a hole's ground footprint is far too
 * small for that to distort the topology `pointInPolygon` tests.
 */
export const shapeAt = (shapes: HoleShapeFeature[], point: LatLon): HoleShapeFeature | null => {
  for (let index = shapes.length - 1; index >= 0; index -= 1) {
    const shape = shapes[index];
    const ring = shape.ring.map((vertex) => ({ x: vertex.lon, y: vertex.lat }));
    if (pointInPolygon({ x: point.lon, y: point.lat }, ring)) return shape;
  }
  return null;
};

export type CourseHoleMapModalProps = {
  visible: boolean;
  onClose: () => void;
  apiBaseUrl: string;
  token: string | null | undefined;
  courseId: string | null;
  /**
   * Every hole the course has, so holes with no geometry can still be paged to. Without it
   * only mapped holes are reachable, which excludes exactly the holes worth editing.
   */
  holeNumbers?: number[];
  /** The club's own position, used to frame a hole that has nothing marked yet. */
  center?: LatLon | null;
  /** Whether marking positions is offered. Admin and web only; see the caller. */
  editable?: boolean;
  /**
   * What the window was opened to look at. `evaluation` starts with OSM and every evaluation
   * layer on and the provider layers off — the comparison first, everything else on request.
   * The models page opens it this way from a model's holdout course.
   */
  openedFor?: 'evaluation';
  /**
   * The hole to open on, when the caller knows one — from a URL, say. Seeded once when the
   * modal opens; after that, paging inside owns which hole is shown, so re-supplying this
   * later (the caller's own URL syncing to `onHoleChange` echoing back in, for instance)
   * cannot fight the admin's own navigation.
   */
  initialHole?: number | null;
  /**
   * Fires whenever the displayed hole changes, while the modal is open. For a caller that
   * wants to keep something else — a URL — in sync with where the admin has paged to.
   */
  onHoleChange?: (holeNumber: number | null) => void;
};

/**
 * The picture drawn under the shapes — purely the background, independent of which shapes
 * are shown. Matching `IMAGERY_PROVIDER_NAME`/`IMAGERY_STAC_PROVIDER_NAME` in
 * course-analysis-service's `providers.py`. "Drawing" is not a provider; it shows no picture
 * at all, with the flat cartographic fills carrying the shapes instead.
 */
type Basemap = 'esri-worldimagery' | 'lantmateriet-orto' | 'geodanmark-orto' | 'drawing';
const BASEMAPS: Basemap[] = [
  'esri-worldimagery',
  'lantmateriet-orto',
  'geodanmark-orto',
  'drawing',
];

/**
 * Which shape sources are drawn, independent of the basemap picture. Unlike the basemap this
 * is a set, not a single pick — combining a provider's satellite detections with OSM's own
 * import is a legitimate way to look at a hole, not just a step on the way to picking one.
 */
type SourceOption =
  | 'esri-worldimagery'
  | 'lantmateriet-orto'
  | 'geodanmark-orto'
  | 'osm'
  | CandidateSource
  | EvaluationSource;
const SOURCE_OPTIONS: SourceOption[] = [
  'esri-worldimagery',
  'lantmateriet-orto',
  'geodanmark-orto',
  'osm',
];

/**
 * The two detectors' raw output, offered as sources of their own.
 *
 * These are not published boundaries and never become them by being looked at: they are what a
 * run returned before promotion pruned it, which is the only form in which the classical
 * detector and the model can be compared on one hole — promotion drops exactly the detections
 * that landed on ground a stronger source already describes, which on a well-mapped course are
 * the correct ones. Off by default; nothing draws them until an admin asks.
 */
type CandidateSource = `candidate-${string}`;

/**
 * One switch per run worth telling apart: `candidate-classical`, and `candidate-model:<version>`
 * for each model the fleet ran. Built from the run rather than fixed, because how many models
 * exist is a fact about the machines analysing, not about this screen — see `candidateKey`.
 */
const candidateSource = (key: string): CandidateSource => `candidate-${key}`;

/**
 * An evaluation's polygons, with the matcher's verdict on each, offered as a source of their
 * own: `evaluation-classical`, and `evaluation-model:<version>` per model scored on this course.
 * Unlike a candidate these were never proposals — they are the polygons behind a precision and
 * a recall, coloured by whether OSM agreed. Off by default, like the candidates.
 */
type EvaluationSource = `evaluation-${string}`;
const evaluationSource = (key: string): EvaluationSource => `evaluation-${key}`;

/** How a verdict is drawn: OSM agreed, OSM has it and the detector missed it, or the detector alone. */
const OUTCOME_COLORS: Record<string, string> = {
  [MATCHED]: appColors.status.success,
  [MISSED]: appColors.status.warningBorder,
  [FALSE_POSITIVE]: appColors.status.danger,
};

/** The detectors offered to run, in the order the buttons appear. */
const DETECTORS = ['classical', 'model'] as const;
type Detector = (typeof DETECTORS)[number];

/**
 * The job kinds Analyse can be asked for, in the order the chips appear.
 *
 * A round is a set of jobs, and pressing Analyse used to mean all of them: two detections per
 * imagery provider, a model detection per checkpoint (twenty minutes each on a CPU), the
 * canopy, the elevations, the pictures. Most of the time what is wanted is one of those —
 * re-cut Lantmäteriet's pictures, re-run the canopy after fixing the credential — so the chips
 * are a filter over what the fleet can do rather than a menu of separate actions. Everything
 * on is the default, and is exactly what the automatic sweep asks for.
 */
const JOB_KINDS = ['detection', 'canopy-height', 'elevation', 'imagery-products'] as const;
type JobKind = (typeof JOB_KINDS)[number];

/** Chosen sets, as the analyse request wants them: an empty list means "all of that sort". */
const chosenOrAll = <T extends string>(chosen: readonly T[], offered: readonly T[]): string[] =>
  chosen.length === offered.length ? [] : [...chosen];

/** Add or remove one value, never leaving the set empty — an empty set would mean "everything". */
const toggled = <T extends string>(chosen: readonly T[], value: T): T[] => {
  const next = chosen.includes(value) ? chosen.filter((v) => v !== value) : [...chosen, value];
  return next.length === 0 ? [value] : next;
};

/**
 * A difference between two lengths, with its sign kept: "+12 m", "−4 yd", "0 m".
 *
 * The sign is the whole content — it says which side of the scorecard the pointer is on — so
 * it is written explicitly rather than left to the minus sign a negative number would carry.
 */
const signedDistance = (deltaMeters: number, unit: DistanceUnit): string => {
  const magnitude = formatHoleDistance(Math.abs(deltaMeters), unit);
  if (Math.round(deltaMeters) === 0) return magnitude;
  return `${deltaMeters > 0 ? '+' : '−'}${magnitude}`;
};

/** How much ground to show around the club when a hole has no geometry at all. */
const FALLBACK_SPAN_METERS = 500;

/**
 * A stable empty tee list for the course-overview page. That page draws no single active hole —
 * it frames on every hole at once (see `overviewFrame`) and lets the numbered markers carry it —
 * so a fresh `[]` here every render would needlessly invalidate the canvas's fit memo.
 */
const NO_TEES: TeePoint[] = [];

/**
 * Tighter than {@link FALLBACK_SPAN_METERS}: a predicted centre is a specific neighbouring
 * hole rather than "somewhere on the property", so there is no need to zoom out as far to
 * find it.
 *
 * Applied only to an *adjacent* hole's position. A guess taken from two or more holes away is
 * a direction rather than a place — the hole could be most of a fairway from it — so that one
 * keeps the wider span and stays centred on the nearest mapped hole instead.
 */
const PREDICTED_SPAN_METERS = 300;

/**
 * Tighter than the map canvas's own default framing margins (a quarter of the width, a tenth
 * of the length) — roughly half of each. Applied only once a hole has a confirmed tee and
 * green; see `holeIsMapped`.
 */
const MAPPED_HORIZONTAL_PAD = 0.12;
const MAPPED_VERTICAL_PAD = 0.05;

const ZOOM_STEP = 1.6;

/**
 * Hole-by-hole view of a course's mapped geometry, for administrators.
 *
 * The in-round hole view answers "where am I hitting"; this answers "is this course mapped
 * correctly". Hence the differences: it pages through every hole rather than showing the one
 * being played, and it lists the actual coordinates and their provenance beside the picture,
 * because a wrong position is far easier to spot as a number next to a map than as a marker
 * in the wrong place.
 *
 * With `editable` it also answers "map it, then". A course that OpenStreetMap never covered
 * and that nobody has walked with tracing on has no geometry at all and no way to acquire
 * any — the imagery analyser needs marked tees and greens before it can find bunkers or a
 * green boundary, so the course stays blank forever. Clicking the positions in breaks that,
 * which is why the editor exists here rather than as a separate screen: this is where the
 * gap is noticed.
 *
 * All the course's features are fetched once and paged through client-side — a course is at
 * most a few dozen rows, and refetching per hole would make the arrows feel broken.
 */
export function CourseHoleMapModal({
  visible,
  onClose,
  apiBaseUrl,
  token,
  courseId,
  holeNumbers = [],
  center = null,
  editable = false,
  openedFor,
  initialHole = null,
  onHoleChange,
}: CourseHoleMapModalProps) {
  const { t } = useI18n();
  const distanceUnit = useDistanceUnit();
  const { height: windowHeight } = useWindowDimensions();
  const { features, shapes, centrelines, lines, loading, refresh, review } = useHoleFeatures(
    apiBaseUrl,
    token,
    visible ? courseId : null,
    { includeUnserved: true }
  );
  const { save, saving } = useManualHoleFeatures(apiBaseUrl, token, courseId);
  // The stakes behind the out-of-bounds lines, each drawn as a short stroke along the
  // heading the mapper faced, so a line that slid onto the wrong edge is visible against
  // the fixes it came from. Admin only; everybody else gets an empty list.
  const { stakes } = useCourseStakes(apiBaseUrl, token, visible ? courseId : null);
  // The scorecard side of the hole — par and the length someone entered — which the geometry
  // read above does not carry. Fetched only while open, like the rest.
  const courseHoles = useCourseHoles(apiBaseUrl, token, visible ? courseId : null);
  // Only for the Lantmateriet basemap option — the picture, not the diagnostic panel's own
  // fetch (CourseAnalysisStatusPanel), which is a separate component with its own instance
  // of this hook. Held off the same way that one is: no point spending the request before
  // the map that would show it is even open.
  const { status: analysisStatus, loading: analysisStatusLoading } = useCourseAnalysisStatus(
    apiBaseUrl,
    token,
    courseId,
    visible
  );
  const [index, setIndex] = useState(0);
  // The first page is a whole-course overview rather than hole 1, unless the caller asked to
  // open on a specific hole (`initialHole`) — then there is a place to be and no reason to
  // detour through the overview. Paging back from hole 1 returns here.
  const [overview, setOverview] = useState(initialHole == null);
  const [editing, setEditing] = useState(false);
  const [arming, setArming] = useState<'tee' | 'green' | null>(null);
  /**
   * The ground under the pointer while a marker is armed — what the measuring line is drawn
   * from. Web only (see `onHoverPoint`), and null the rest of the time, which is what takes
   * the line off the map.
   */
  const [hoverPoint, setHoverPoint] = useState<LatLon | null>(null);
  const [draft, setDraft] = useState<HoleDraft | null>(null);
  const [view, setView] = useState<ViewTransform>(IDENTITY_VIEW);
  const [message, setMessage] = useState<string | null>(null);
  /** A coordinate typed into the go-to field, once it has been read successfully. */
  const [manualCenter, setManualCenter] = useState<LatLon | null>(null);
  const [gotoText, setGotoText] = useState('');
  const [gotoInvalid, setGotoInvalid] = useState(false);
  /**
   * Boundary kinds switched off, which basemap picture is shown, and which shape sources are
   * drawn.
   *
   * All three persist across holes on purpose, unlike the frame: someone checking every
   * bunker on a course is asking the same question eighteen times, and having to switch the
   * other layers off again on each hole is how a review gets abandoned halfway round.
   */
  const [hiddenKinds, setHiddenKinds] = useState<string[]>([]);
  /**
   * Lantmateriet first: it is the imagery the analysis actually cut its shapes from, so a
   * shape drawn over it can be judged against the ground it came from. Falls back to Esri
   * below when this course has no stored picture — every course has Esri, not every course
   * has been through the mosaic.
   */
  const [basemap, setBasemap] = useState<Basemap>('lantmateriet-orto');
  /** Set once the admin picks a basemap themselves, so the fallback below stops overriding. */
  const basemapPicked = useRef(false);
  const [sourceFilter, setSourceFilter] = useState<SourceOption[]>(SOURCE_OPTIONS);
  /**
   * What the Analyse button asks for: which kinds of job, which detectors, which imagery
   * providers. Null means "everything offered" — the state only holds a narrowing once the
   * person has made one, so a fleet that gains a capability widens the default by itself.
   */
  const [chosenKinds, setChosenKinds] = useState<JobKind[] | null>(null);
  const [chosenDetectors, setChosenDetectors] = useState<Detector[] | null>(null);
  const [chosenProviders, setChosenProviders] = useState<string[] | null>(null);
  /**
   * Bumped after a run is queued so the candidate layer re-reads. Not a poll: the run takes
   * minutes, and paging away and back is the natural way to look again.
   */
  const [candidateReload, setCandidateReload] = useState(0);

  // Admin-only and admin-gated at the BFF, so it is asked for only where the tools are
  // shown: a player's hole view has no business fetching a model's unreviewed guesses.
  const { shapes: candidateShapes, runs: candidateRuns, loading: candidatesLoading } = useAnalysisCandidates(
    apiBaseUrl,
    token,
    visible && editable ? courseId : null,
    visible && editable,
    candidateReload
  );
  // The same gate as the candidates, for the same reason: a false positive drawn on a player's
  // hole view would read as the course's own map.
  const { shapes: evaluationShapes, runs: evaluationRuns } = useEvaluationFeatures(
    apiBaseUrl,
    token,
    visible && editable ? courseId : null,
    visible && editable
  );
  /** The shape a tap last landed on, shown as a callout instead of making the admin scroll
   * the list below to find it among however many the hole has. Looked up by id each render
   * rather than held as a snapshot, so it drops out on its own if the shape is filtered away. */
  const [selectedShapeId, setSelectedShapeId] = useState<string | null>(null);

  /**
   * The holes that can be paged to.
   *
   * The course's own holes plus any hole that has geometry — the second half catches a
   * feature attributed to a hole the scorecard does not have, which is worth seeing rather
   * than hiding. Falls back to mapped holes alone when the caller passes no scorecard, which
   * is the read-only behaviour this screen had before.
   */
  const holes = useMemo(
    () =>
      [...new Set([...holeNumbers, ...features.map((feature) => feature.holeNumber)])]
        .filter((number) => number > UNNUMBERED_HOLE)
        .sort((a, b) => a - b),
    [features, holeNumbers]
  );

  // Clamped rather than reset: reloading after an import should keep the admin on the hole
  // they were inspecting, unless it no longer exists.
  const safeIndex = holes.length === 0 ? 0 : Math.min(index, holes.length - 1);
  const holeNumber = holes[safeIndex] ?? null;

  /**
   * Jumps to `initialHole` once, the first time it is found in `holes`.
   *
   * Retried on every `holes` recompute rather than just on `visible`, because `holes` starts
   * out as whatever `holeNumbers` alone contains and only gains the fetched holes afterwards
   * — a hole reachable solely through a feature attributed to it would otherwise miss the one
   * moment this looks. The ref stops it from firing again once it has, which is what lets the
   * admin page away from `initialHole` afterwards without being pulled back to it.
   */
  const seededInitialHoleRef = useRef(false);
  useEffect(() => {
    if (!visible) {
      seededInitialHoleRef.current = false;
      // Reset for the next open: the overview leads again unless the caller names a hole.
      setOverview(initialHole == null);
      return;
    }
    if (seededInitialHoleRef.current || initialHole == null) return;
    const target = holes.indexOf(initialHole);
    if (target < 0) return;
    seededInitialHoleRef.current = true;
    setIndex(target);
    setOverview(false);
  }, [visible, initialHole, holes]);

  // Reported only while open: a stale `holeNumber` lingering in state right after close is not
  // a navigation the caller's URL should end up reflecting.
  useEffect(() => {
    if (visible) onHoleChange?.(holeNumber);
  }, [visible, holeNumber, onHoleChange]);

  const holeFeatures = useMemo(
    () => features.filter((feature) => feature.holeNumber === holeNumber),
    [features, holeNumber]
  );

  /** The known tee or green closest to a hole number, whichever the course actually has. */
  const positionOfHole = useCallback(
    (number: number | undefined): LatLon | null => {
      if (number === undefined) return null;
      const holeFeaturesFor = features.filter((feature) => feature.holeNumber === number);
      const green = holeFeaturesFor.find((feature) => feature.kind === 'green');
      if (green) return { lat: green.lat, lon: green.lon };
      const tee = holeFeaturesFor.find((feature) => feature.kind === 'tee');
      return tee ? { lat: tee.lat, lon: tee.lon } : null;
    },
    [features]
  );

  /**
   * The best guess at where an unmapped hole actually is, and how far afield it was found.
   *
   * Holes are walked in playing order, so the green just finished is usually a short walk
   * from the next tee — a far better first guess than the club's own coordinate, which can
   * sit anywhere on the property and be hundreds of metres from the hole being mapped. The
   * following hole is asked next, since a hole is also a short walk from the tee after it.
   *
   * Both neighbours being blank used to end the search and send the map back to the clubhouse.
   * That is the wrong moment to give up: it happens exactly when an admin is working forwards
   * through a stretch of unmapped holes, where the nearest mapped hole is two or three back and
   * still far closer than the club's coordinate. So the search widens a hole at a time, nearest
   * first, previous before following at each step.
   *
   * `step` is how many holes away the answer came from, and the framing uses it — see
   * {@link PREDICTED_SPAN_METERS}. An adjacent green is a tight, specific guess; one four holes
   * back is a direction rather than a place, and framing it as tightly would crop the hole out.
   */
  const predictedCenter = useMemo(() => {
    for (let step = 1; step < Math.max(holes.length, 2); step++) {
      const behind = positionOfHole(holes[safeIndex - step]);
      if (behind) return { position: behind, step };
      const ahead = positionOfHole(holes[safeIndex + step]);
      if (ahead) return { position: ahead, step };
    }
    return null;
  }, [holes, positionOfHole, safeIndex]);

  /**
   * Every other hole that already has both a tee and a green marked, tee to green — so paging
   * blind through eighteen holes is not the only way to see which are already mapped.
   *
   * Hole {@link UNNUMBERED_HOLE} is left out, the same way `holes` and the counter leave it out.
   * It is not a hole: it collects the ways whose ref could not be trusted and every bunker, pond
   * and green that landed on no hole at all, so a marker there is a badge on ground nobody can
   * name — and a line through it is worse, since its tees and greens are ordinalled by separate
   * counters and pairing them would be guesswork. Those features are not lost by this: a hole-0
   * bunker still draws on whichever hole it sits beside, through `shapesNearHole`, which selects
   * by proximity rather than by hole number.
   */
  const allHoleMarkers = useMemo(() => {
    const byHole = new Map<
      number,
      {
        tee: LatLon | null;
        teeOrdinal: number;
        green: LatLon | null;
        /** The hole's placed bends, by ordinal, so they end up in playing order. */
        turns: Map<number, LatLon>;
      }
    >();
    for (const feature of features) {
      if (feature.holeNumber <= UNNUMBERED_HOLE) continue;
      const entry = byHole.get(feature.holeNumber) ?? {
        tee: null,
        teeOrdinal: Infinity,
        green: null,
        turns: new Map<number, LatLon>(),
      };
      if (feature.kind === 'tee' && feature.ordinal < entry.teeOrdinal) {
        entry.tee = { lat: feature.lat, lon: feature.lon };
        entry.teeOrdinal = feature.ordinal;
      }
      if (feature.kind === 'green') {
        entry.green = { lat: feature.lat, lon: feature.lon };
      }
      // Every hole's bends, not just this one's: a neighbouring dogleg drawn as a chord runs
      // through the trees between its ends. Only the placed ones are stored, and a bend
      // nobody placed sits on the straight line anyway — so this needs no par, and a hole
      // nobody has bent draws the chord it always did.
      if (feature.kind === 'turn') {
        entry.turns.set(feature.ordinal, { lat: feature.lat, lon: feature.lon });
      }
      byHole.set(feature.holeNumber, entry);
    }
    const markers: KnownHoleMarker[] = [];
    for (const [number, entry] of byHole) {
      if (entry.tee && entry.green) {
        markers.push({
          holeNumber: number,
          tee: entry.tee,
          green: entry.green,
          turns: [...entry.turns.entries()]
            .sort(([a], [b]) => a - b)
            .map(([, point]) => point),
        });
      }
    }
    return markers.sort((a, b) => a.holeNumber - b.holeNumber);
  }, [features]);

  /** Every mapped hole but the one being viewed — drawn faintly under the active hole. */
  const otherHoles = useMemo(
    () => allHoleMarkers.filter((marker) => marker.holeNumber !== holeNumber),
    [allHoleMarkers, holeNumber]
  );

  /**
   * The one {@link otherHoles} leaves out, so the canvas can number it too. Taken from the same
   * list rather than from the draft: a hole is only numbered on the map once both its ends are
   * known, and that is exactly what earned it a place in `allHoleMarkers`. A hole mid-edit, or
   * one with no tee or green yet, is simply absent — the same as it was before.
   */
  const currentHoleMarker = useMemo(
    () => allHoleMarkers.find((marker) => marker.holeNumber === holeNumber) ?? null,
    [allHoleMarkers, holeNumber]
  );

  // The draft follows the hole, and is rebuilt from the server after every save so what is
  // drawn is always what is stored rather than what was clicked.
  useEffect(() => {
    if (holeNumber === null) {
      setDraft(null);
      return;
    }
    setDraft(draftFromFeatures(holeNumber, features));
  }, [features, holeNumber]);

  // The overview fits the whole property and a hole page fits one hole, so a zoom or a pan
  // belongs to whichever of the two it was made on. Stepping between them keeps the same
  // `holeNumber`, so the reset below never fires for it.
  useEffect(() => {
    setView(IDENTITY_VIEW);
  }, [overview]);

  useEffect(() => {
    // A new hole is a new frame; carrying the previous hole's pan over would open it
    // looking at the wrong piece of ground.
    setView(IDENTITY_VIEW);
    // A typed centre belongs to the hole it was typed on; carrying it to the next one would
    // silently frame that hole somewhere it has nothing to do with.
    setManualCenter(null);
    setGotoText('');
    setGotoInvalid(false);
    setArming(null);
    // A point from the hole just left would put the measuring line over the wrong ground if the
    // pointer never moves before the next tool is armed.
    setHoverPoint(null);
  }, [holeNumber]);

  const dirty = useMemo(
    () => (draft ? isDraftDirty(draft, features) : false),
    [draft, features]
  );

  /** Tees and green as the canvas wants them, from a set of draft markers. */
  const teesAndGreenOf = (markers: HoleDraft['markers']) => {
    const tees: TeePoint[] = markers
      .filter((marker) => marker.kind === 'tee')
      .map((marker) => ({ lat: marker.lat, lon: marker.lon, label: marker.name }));
    const greenMarker = markers.find((marker) => marker.kind === 'green');
    return { tees, green: greenMarker ? { lat: greenMarker.lat, lon: greenMarker.lon } : null };
  };

  /** What the map draws: the draft while editing, the stored geometry otherwise. */
  const shown = useMemo(
    () =>
      teesAndGreenOf(
        editing && draft ? draft.markers : draftFromFeatures(holeNumber ?? 0, features).markers
      ),
    [draft, editing, features, holeNumber]
  );

  /** This hole's scorecard row: its par, the per-tee lengths, and the hole's own. */
  const holeCard = useMemo(
    () => courseHoles.find((hole) => hole.number === holeNumber) ?? null,
    [courseHoles, holeNumber]
  );

  /** How many bends this hole has: one on a par 4, two on a par 5, none on a par 3. */
  const turnCount = useMemo(() => turnPointCount(holeCard?.par), [holeCard]);

  /**
   * The back tee — the one furthest from the green.
   *
   * The hole measured from anywhere else is a shorter version of the same line, so this is
   * the end the playing line is drawn from and the end its length is quoted from, which is
   * the tee a scorecard length is measured from too.
   */
  const backTee = useMemo(() => {
    if (!shown.green || shown.tees.length === 0) return null;
    return shown.tees.reduce<LatLon>(
      (furthest, tee) =>
        distanceMeters(tee, shown.green!) > distanceMeters(furthest, shown.green!)
          ? { lat: tee.lat, lon: tee.lon }
          : furthest,
      { lat: shown.tees[0].lat, lon: shown.tees[0].lon }
    );
  }, [shown]);

  /**
   * The line the hole is played along as the draft has it: back tee, its bends, green.
   *
   * The bends nobody has dragged yet are worked out from the ones somebody has — see
   * {@link resolveTurnPoints} — so a par 5 with one point moved still reads as a line rather
   * than as a kink through a spot the admin has not looked at.
   */
  const drawnPath = useMemo(
    () =>
      holePath(
        backTee,
        shown.green,
        turnCount,
        draft ? placedTurnPoints(draft, turnCount) : new Map()
      ),
    [backTee, draft, shown.green, turnCount]
  );

  /**
   * Whether anybody has actually placed a bend on this hole.
   *
   * What decides which line the read-only map draws. A hole with placed turning points is
   * drawn along them, because that is the line the header's measured length was taken from
   * and a picture that disagreed with the number under the title would be the more confusing
   * of the two. A hole with none falls back to whatever line is stored — the bends
   * {@link drawnPath} works out for it are on the chord anyway, and are nothing anybody has
   * stated.
   */
  const hasPlacedTurns = useMemo(
    () => (draft ? placedTurnPoints(draft, turnCount).size > 0 : false),
    [draft, turnCount]
  );

  /**
   * The hole's stored playing line — the tee-to-green line bent through the middle of the
   * hole, where anyone has said where that is. Empty for a hole nobody has, and then the map
   * falls back to the straight chord it has always drawn.
   */
  const playingLine = useMemo(
    () => (holeNumber === null ? [] : playingLineFor(centrelines, holeNumber)),
    [centrelines, holeNumber]
  );

  /**
   * The saved geometry, regardless of what is being edited right now.
   *
   * Used only for framing (see `frame` below) — never for what is drawn, which is `shown`.
   */
  const savedShown = useMemo(
    () => teesAndGreenOf(draftFromFeatures(holeNumber ?? 0, features).markers),
    [features, holeNumber]
  );

  /**
   * A tee and a green are both on record — the hole is a confirmed picture, not a guess.
   *
   * Used to tighten the frame (see `frame`'s pad fractions below): an unmapped hole wants the
   * wide, generous view that doubles as a course overview, but once a hole is confirmed there
   * is no reason to keep sharing the frame with whatever is next door.
   */
  const holeIsMapped = savedShown.tees.length > 0 && savedShown.green !== null;

  /**
   * Every mapped boundary that belongs to this hole's picture, in draw order.
   *
   * Candidates join the published rows here and nowhere else — in particular not in
   * `frameShapes` below. A detector's raw output includes components reaching well past the
   * hole, and letting them decide the zoom would pull the view out to the whole course the
   * moment the layer is switched on.
   */
  const holeShapes = useMemo(
    () => shapesForHole([...shapes, ...candidateShapes, ...evaluationShapes], holeNumber, shown.tees, shown.green),
    [candidateShapes, evaluationShapes, holeNumber, shapes, shown]
  );

  /**
   * The same picture, but built from the saved geometry rather than the live draft, and
   * clipped to the hole's own corridor (see `framingRingsForHole`).
   *
   * An unmapped hole falls back to every shape on the course (see `shapesForHole`), and the
   * instant a tee is placed that set collapses down to just the nearby ones. Feeding that
   * straight into the map's frame would snap the view in around the click the moment it
   * happens — this keeps the frame on what was on screen when editing started instead.
   */
  const frameShapes = useMemo(
    () => framingRingsForHole(shapes, holeNumber, savedShown.tees, savedShown.green),
    [holeNumber, shapes, savedShown]
  );

  /** What the map's frame is fit to — see {@link HoleMapCanvas}'s `frame` prop. */
  const frame = useMemo(
    () => ({ tees: savedShown.tees, green: savedShown.green, polygons: frameShapes }),
    [frameShapes, savedShown]
  );

  /**
   * The overview page's frame: every mapped hole at once, so the fit zooms out to the whole
   * course. Greens ride in as single-point rings — the canvas fits `tees` plus every polygon
   * vertex — which is the cheapest way to fold both ends of every hole into one bound.
   *
   * The bends ride in the same way. A dogleg's turning point can sit well outside the box its
   * own two ends make, and a fit that left it out would draw the hole running off the edge of
   * the overview it is supposed to be framed by.
   */
  const overviewFrame = useMemo(
    () => ({
      tees: allHoleMarkers.map((marker) => ({ ...marker.tee, label: null })),
      green: null,
      polygons: allHoleMarkers.map((marker) => ({
        ring: [marker.green, ...(marker.turns ?? [])],
      })),
    }),
    [allHoleMarkers]
  );

  /**
   * `holeShapes` narrowed to the chosen sources — independent of the basemap picture, which
   * only decides what is drawn underneath.
   *
   * A shape not attributable to any of the three sources on offer — manually placed, or a
   * bare GPS fix — is nobody's to claim, so it stays regardless of what is picked.
   */
  const providerShapes = useMemo(
    () =>
      holeShapes.filter((shape) => {
        if (shape.source === CANDIDATE_SOURCE) {
          // A candidate's "provider" is its detector, and its model where it had one — that is
          // what a reader switches on.
          return shape.sourceProvider
            ? sourceFilter.includes(candidateSource(shape.sourceProvider))
            : false;
        }
        if (shape.source === EVALUATION_SOURCE) {
          return shape.sourceProvider ? sourceFilter.includes(evaluationSource(shape.sourceProvider)) : false;
        }
        if (shape.source === 'satellite') {
          return shape.sourceProvider
            ? sourceFilter.includes(shape.sourceProvider as SourceOption)
            : true;
        }
        if (shape.source === 'osm') return sourceFilter.includes('osm');
        return true;
      }),
    [sourceFilter, holeShapes]
  );

  /**
   * The candidate chips to offer: one per detector that actually returned something for this
   * course. Derived from the runs rather than hardcoded, so a course nobody has run the model
   * over shows no model switch to turn on and find empty.
   */
  const candidateOptions = useMemo(() => {
    const seen = new Map<CandidateSource, { detector: string; model: string | null; count: number }>();
    for (const run of candidateRuns) {
      if (run.count === 0) continue;
      const option = candidateSource(candidateKey(run));
      const existing = seen.get(option);
      seen.set(option, {
        detector: run.detector,
        // The name a person can say — the registry's two-word nickname where the model is
        // registered, and the raw version where it is not. Null for the classical detector,
        // and for a model run that took whichever checkpoint fitted the imagery.
        model: run.modelNickname ?? run.providerVersion,
        count: (existing?.count ?? 0) + run.count,
      });
    }
    return [...seen.entries()].map(([option, value]) => ({ option, ...value }));
  }, [candidateRuns]);

  /**
   * One evaluation chip per detector scored on this course, its verdicts tallied across every
   * kind: how many polygons OSM agreed with, how many it never had, how many it had that the
   * detector missed. The tally is the chip's label — a precision of 0.18 as three numbers a
   * person can see the shape of before switching the layer on.
   */
  const evaluationOptions = useMemo(() => {
    const seen = new Map<
      EvaluationSource,
      { detector: string; model: string | null; matched: number; missed: number; falsePositive: number }
    >();
    for (const run of evaluationRuns) {
      const option = evaluationSource(evaluationKey(run));
      const existing = seen.get(option) ?? {
        detector: run.detector,
        model: run.modelNickname ?? run.modelVersion,
        matched: 0,
        missed: 0,
        falsePositive: 0,
      };
      seen.set(option, {
        ...existing,
        matched: existing.matched + run.counts.matched,
        missed: existing.missed + run.counts.missed,
        falsePositive: existing.falsePositive + run.counts.falsePositive,
      });
    }
    return [...seen.entries()].map(([option, value]) => ({ option, ...value }));
  }, [evaluationRuns]);

  // Opened to look at an evaluation: OSM and every evaluation layer on, the published
  // provider layers off, so the first picture is the comparison rather than everything at once.
  const focusedEvaluation = useRef(false);
  useEffect(() => {
    if (!visible) {
      focusedEvaluation.current = false;
      return;
    }
    if (openedFor !== 'evaluation' || focusedEvaluation.current || evaluationOptions.length === 0) return;
    focusedEvaluation.current = true;
    setSourceFilter(['osm', ...evaluationOptions.map((o) => o.option)]);
  }, [visible, openedFor, evaluationOptions]);

  /** The switches offered: one per kind actually found on this hole, with its tally. */
  const layers = useMemo(() => layersOf(providerShapes), [providerShapes]);

  const shownShapes = useMemo(
    () => visibleShapes(providerShapes, hiddenKinds),
    [hiddenKinds, providerShapes]
  );

  /**
   * The rings handed to the map.
   *
   * Over a picture — live tiles or the stored mosaic — they are outlines with a wash inside,
   * so the ground stays readable through them. With no picture there is nothing to read
   * through, and the flat cartographic fills —
   * the same ones the in-round yardage-book drawing uses — are what make the shapes tell
   * apart at a glance.
   */
  const showImagery = basemap === 'esri-worldimagery';

  /** The stored picture, only while a basemap that has one is actually the one picked —
   * switching away and back should not keep an old image drawn under the wrong choice.
   * Every national orthophoto stores per course; Esri's licence forbids it and drawing has
   * no picture at all. */
  const storedBasemap = isNationalOrtho(basemap) ? analysisStatus?.basemapImage ?? null : null;

  /**
   * Nothing stored for this course, so the Lantmateriet default cannot stand: fall back to the
   * basemap that always has a picture. Only until the admin picks one themselves.
   *
   * Waits for the status read to have both started and finished. Before it starts, a course
   * with a stored picture is indistinguishable from one without — deciding then would drop
   * every course to Esri and never look again, since by the time the picture arrived the
   * choice would already have been made. A read that comes back unreadable leaves `status`
   * null with no error, which is the same answer as nothing stored: fall back.
   */
  const basemapStatusRequested = useRef(false);
  useEffect(() => {
    if (basemapPicked.current) return;
    if (analysisStatusLoading) {
      basemapStatusRequested.current = true;
      return;
    }
    if (!basemapStatusRequested.current || analysisStatus?.basemapImage) return;
    setBasemap('esri-worldimagery');
  }, [analysisStatus, analysisStatusLoading]);

  /** Is anything drawn beneath the shapes — live tiles or the stored mosaic. Decides how they
   * are drawn: see `polygons`. */
  const hasPicture = showImagery || storedBasemap !== null;

  const polygons = useMemo<MapOverlayPolygon[]>(
    () =>
      shownShapes.map((shape) => {
        // An evaluation polygon is coloured by its verdict, not its kind: the question it
        // answers is "did OSM agree", and the kind is already the chip it was switched on by.
        const verdict = shape.outcome ? OUTCOME_COLORS[shape.outcome] : undefined;
        if (verdict) return { id: shape.id, ring: shape.ring, color: verdict };
        const outline = OVERLAY_COLORS[shape.kind] ?? appColors.text.onMedia;
        const flat = DRAWING_FILLS[shape.kind];
        return hasPicture || !flat
          ? { id: shape.id, ring: shape.ring, color: outline }
          : { id: shape.id, ring: shape.ring, color: flat.stroke, fill: flat.fill, fillOpacity: 1 };
      }),
    [hasPicture, shownShapes]
  );

  /**
   * The hole's out-of-bounds lines and the stakes behind them. The line is white and dashed
   * as the stakes are white; a stake is a short stroke from where the phone stood towards
   * the side the mapper faced, amber while nobody has reviewed it, red once rejected.
   */
  const polylines = useMemo<MapOverlayLine[]>(() => {
    if (holeNumber === null) return [];
    const drawn: MapOverlayLine[] = lines
      .filter((line) => line.holeNumber === holeNumber)
      .map((line) => ({
        id: line.id,
        path: line.path,
        color: appColors.text.onMedia,
        dash: '10,7',
        strokeWidth: 2.5,
      }));
    for (const stake of stakes) {
      if (stake.holeNumber !== holeNumber) continue;
      const metres = stake.headingDegrees === null ? 0 : 8;
      const radians = ((stake.headingDegrees ?? 0) * Math.PI) / 180;
      const end = {
        lat: stake.lat + (metres * Math.cos(radians)) / 111320,
        lon: stake.lon + (metres * Math.sin(radians)) / (111320 * Math.cos((stake.lat * Math.PI) / 180)),
      };
      drawn.push({
        id: `stake-${stake.id}`,
        // A stake with no heading is drawn as a dot: a zero-length stroke with round caps.
        path: [{ lat: stake.lat, lon: stake.lon }, end],
        color:
          stake.reviewStatus === 'rejected'
            ? OUTCOME_COLORS['false-positive'] ?? appColors.text.warm
            : appColors.text.warm,
        strokeWidth: 4,
      });
    }
    return drawn;
  }, [holeNumber, lines, stakes]);

  /** "Bunker", "Water" — falling back to the stored kind for anything not translated yet. */
  const kindLabel = (kind: string) => {
    const key = `courseHoleMap.shapeKind_${kind}`;
    const label = t(key);
    return label === key ? kind : label;
  };

  const describeShape = (shape: HoleShapeFeature) =>
    t('courseHoleMap.shapeMeta', {
      area: formatArea(ringAreaSquareMeters(shape.ring), distanceUnit),
      points: shape.ring.length,
      hole: shape.holeNumber,
    });

  // Looked up by id rather than kept as its own state, so a shape hidden via the layer bar
  // (or dropped by a refresh) takes the callout with it instead of leaving it pointing at
  // something no longer on the map.
  const selectedShape = shownShapes.find((shape) => shape.id === selectedShapeId) ?? null;

  /** "Tee 2", "Turn 1", "Green" — how one marked point is named in the listings. */
  const pointLabel = (kind: MarkerKind, ordinal: number) => {
    if (kind === 'turn') return t('courseHoleMap.turnPoint', { index: ordinal + 1 });
    const name = t(kind === 'tee' ? 'courseMappingReview.tee' : 'courseMappingReview.green');
    return kind === 'tee' && ordinal > 0 ? `${name} ${ordinal + 1}` : name;
  };

  const describe = (feature: HoleFeature) => {
    const kind = pointLabel(feature.kind, feature.ordinal);
    return feature.name ? `${kind} ${feature.name}` : kind;
  };

  /**
   * How long the hole measures along the line it is played: back tee, round its bends, green.
   *
   * The bends are what the tee-to-green chord was always missing — a dogleg walked as two
   * straight shots is longer than the crow flies, and quoting the crow made every dogleg read
   * as fifty metres shorter than its own scorecard says. On a hole with no bends this is the
   * chord it has always been, from the tee furthest out, which is how the hole view measures
   * it too. It follows a turning point as it is dragged, so the figure in the header is the
   * hole as it stands rather than as it was when the editor opened.
   */
  const holeLength = useMemo(
    () => (drawnPath.length < 2 ? null : formatHoleDistance(pathLengthMeters(drawnPath), distanceUnit)),
    [distanceUnit, drawnPath]
  );

  /**
   * The tees this hole has a stored length for, longest first.
   *
   * Only those: a tee with no length is nothing to select, because the whole point of the chip
   * is the number it puts in the header. One entry per tee even where several sources measured
   * it — {@link bestTeeLength} picks which, and the chip names the source so a card length is
   * never mistaken for ours (CLAUDE.md §19).
   */
  const teeLengthOptions = useMemo(
    () => teeLengthChoices(holeCard?.teeLengths ?? []),
    [holeCard]
  );

  const [selectedTeeId, setSelectedTeeId] = useState<string | null>(null);

  // A tee selection belongs to the hole it was made on. Carrying "Gul" across to the next hole
  // would be defensible, but carrying it to a hole where Gul has no length would leave the
  // header showing a tee whose chip is not on screen.
  useEffect(() => {
    setSelectedTeeId(null);
  }, [holeNumber]);

  const selectedTee = useMemo(
    () => teeLengthOptions.find((option) => option.teeId === selectedTeeId) ?? null,
    [selectedTeeId, teeLengthOptions]
  );

  /**
   * "Par 4 · 312 m · 306 m measured" — the scorecard's own par and length first, then what
   * the mapped tee and green actually measure. The two lengths are worth seeing side by side:
   * a card length that disagrees with the map by fifty metres is how a tee marked on the
   * wrong box is spotted. Either can be missing, and the line is dropped when all three are.
   */
  const holeSubtitle = useMemo(() => {
    const card = courseHoles.find((hole) => hole.number === holeNumber) ?? null;
    const parts: string[] = [];
    if (card?.par) parts.push(t('courseHoleMap.par', { par: card.par }));
    // A chosen tee answers the same question the hole's own length does — how long is this —
    // so it replaces it rather than sitting beside it. Two lengths in one line, one of them
    // for a tee and one for no tee in particular, is the ambiguity the chips exist to remove.
    if (selectedTee) {
      parts.push(
        t('courseHoleMap.teeLength', {
          tee: selectedTee.teeName,
          length: formatHoleDistance(selectedTee.lengthMeters, distanceUnit),
          source: lengthSourceLabel(selectedTee.source, t),
        })
      );
    } else if (card?.lengthMeters) {
      parts.push(formatHoleDistance(card.lengthMeters, distanceUnit));
    }
    if (holeLength) parts.push(t('courseHoleMap.measuredLength', { length: holeLength }));
    return parts.length > 0 ? parts.join(' · ') : null;
  }, [courseHoles, distanceUnit, holeLength, holeNumber, selectedTee, t]);

  /**
   * The line drawn from the pointer while a marker is armed, and what it reads.
   *
   * Placing a tee measures back to the green; placing the green measures back to the tee it
   * would be furthest from, which is the tee a scorecard length is measured from. Alongside
   * the live figure it shows what the scorecard says the nearest tee should be — that
   * comparison is the point of the exercise, and it has to be visible on the way to the tee
   * box rather than only once the pointer is already on it.
   */
  const measure = useMemo(() => {
    if (!editing || !arming || !hoverPoint) return null;

    const anchor =
      arming === 'tee'
        ? shown.green
        : shown.tees.reduce<LatLon | null>(
            (furthest, tee) =>
              furthest === null ||
              distanceMeters(hoverPoint, tee) > distanceMeters(hoverPoint, furthest)
                ? { lat: tee.lat, lon: tee.lon }
                : furthest,
            null
          );
    if (!anchor) return null;

    const metres = distanceMeters(hoverPoint, anchor);
    const parts = [formatHoleDistance(metres, distanceUnit)];

    const nearestTee = nearestTeeByLength(holeCard?.teeLengths ?? [], metres);
    if (nearestTee) {
      parts.push(
        t('courseHoleMap.measureAgainstTee', {
          tee: nearestTee.teeName,
          length: formatHoleDistance(nearestTee.lengthMeters, distanceUnit),
          delta: signedDistance(metres - nearestTee.lengthMeters, distanceUnit),
        })
      );
    } else if (holeCard?.lengthMeters) {
      parts.push(
        t('courseHoleMap.measureAgainstCard', {
          length: formatHoleDistance(holeCard.lengthMeters, distanceUnit),
          delta: signedDistance(metres - holeCard.lengthMeters, distanceUnit),
        })
      );
    }

    return { from: hoverPoint, to: anchor, label: parts.join(' · '), metres };
  }, [arming, distanceUnit, editing, holeCard, hoverPoint, shown, t]);

  /**
   * The map's shape.
   *
   * Square is right on a phone, where the card is narrow and the map is all there is. In the
   * wide card it would be as tall as it is wide and push the tools, the coordinates and the
   * save button off the bottom of a laptop screen — so there it takes half the window height
   * and whatever width the card gives it.
   */
  const canvasStyle = useMemo(
    () =>
      editable
        ? // Never shrunk to fit what is below it — the card scrolls instead. Clicking a tee
          // box means aiming at a shape a few metres across, and a map squeezed to make room
          // for a coordinate listing is a map you cannot aim at.
          { flexShrink: 0, aspectRatio: undefined, height: Math.max(240, windowHeight * 0.45) }
        : { flexShrink: 0 },
    [editable, windowHeight]
  );

  const onMapPress = useCallback(
    (point: LatLon) => {
      if (editing && arming && draft) {
        // A tee whose distance to the green matches one of the scorecard's per-tee lengths is
        // that tee, and lands already labelled. Only on a match: an unnamed tee is a fact
        // nobody has stated, where a guessed colour would read as one somebody had.
        const matched =
          arming === 'tee' && shown.green
            ? matchTeeByLength(holeCard?.teeLengths ?? [], distanceMeters(point, shown.green))
            : null;
        setDraft(placeMarker(draft, arming, point, matched?.teeName ?? null));
        // One click, one marker: leaving it armed made a second look at the map place a
        // second tee nobody asked for.
        setArming(null);
        setHoverPoint(null);
        setMessage(matched ? t('courseHoleMap.teeNamed', { tee: matched.teeName }) : null);
        return;
      }
      // Not placing a marker: a tap is asking what is drawn at that point, not editing it.
      setSelectedShapeId(shapeAt(shownShapes, point)?.id ?? null);
    },
    [arming, draft, editing, holeCard, shown.green, shownShapes, t]
  );

  /**
   * A marker dragged on the map, moved in the draft as each position arrives.
   *
   * The canvas identifies a tee by its index into the `tees` it was given, which is the
   * draft's tee markers in order (see `teesAndGreenOf`) — mapped back to the marker's own
   * key here, so the drag keeps its identity and its server row. A functional update rather
   * than closing over `draft`: a drag delivers many positions per second, and each must move
   * the draft the previous one produced.
   */
  const onMarkerDrag = useCallback((marker: DraggedMarker, point: LatLon) => {
    setDraft((current) => {
      if (!current) return current;
      // A turning point is the one marker that need not exist yet: until it is dragged it is
      // worked out from the straight line, so the first drag is what makes it a marker at all.
      if (marker.kind === 'turn') return placeTurnPoint(current, marker.index, point);
      const dragged =
        marker.kind === 'green'
          ? current.markers.find((candidate) => candidate.kind === 'green')
          : current.markers.filter((candidate) => candidate.kind === 'tee')[marker.index];
      return dragged ? moveMarker(current, markerKey(dragged), point) : current;
    });
  }, []);

  /**
   * Writes a corrected course coordinate.
   *
   * Analysis is requested with it: whatever was analysed before was imagery of the wrong
   * place, so the stored features for this course are answers to a different question.
   */
  const saveCoursePosition = useCallback(async (point: LatLon): Promise<boolean> => {
    if (!courseId || !token) return false;
    try {
      const response = await fetch(`${apiBaseUrl}/api/geodata/courses/${courseId}/position`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ lat: point.lat, lon: point.lon, requestAnalysis: true }),
      });
      return response.ok;
    } catch {
      return false;
    }
  }, [apiBaseUrl, courseId, token]);

  const onSave = useCallback(async () => {
    if (!draft) return;
    setMessage(null);

    // A hole re-carded from a par 5 to a par 4 has one bend now. The second one's stored row
    // is dropped here rather than left behind drawing nothing.
    const toSave = trimTurnPoints(draft, turnCount);

    // Deletions first. A marker removed here has a server row that a save would not touch —
    // the write only upserts what it is given — so leaving them for afterwards would mean a
    // failure halfway through resurrects them on the next refresh.
    const removed = removedFeatureIds(toSave, features);
    for (const featureId of removed) {
      const deleted = await review(featureId, false);
      if (!deleted) {
        setMessage(t('courseHoleMap.saveFailed'));
        return;
      }
    }

    const payload = toManualFeatures(toSave);
    if (payload.length > 0) {
      const saved = await save(payload);
      if (!saved) {
        setMessage(t('courseHoleMap.saveFailed'));
        return;
      }
    }

    // The course's own coordinate frames every hole that has nothing marked yet, and it is
    // where the imagery analyser points its tiles. Marking a hole kilometres away from it is
    // evidence that the coordinate is the thing that is wrong — so it is moved to match, and
    // with it every other hole's starting frame. Re-analysis is requested because whatever
    // was analysed before was a picture of the wrong place.
    const corrected = correctedCoursePosition(
      center,
      toSave.markers.map((marker) => ({ lat: marker.lat, lon: marker.lon }))
    );
    if (corrected) {
      const moved = await saveCoursePosition(corrected);
      // The geometry is already saved; a failure here costs the reframing, not the work.
      setMessage(t(moved ? 'courseHoleMap.savedAndMoved' : 'courseHoleMap.saved'));
      refresh();
      return;
    }

    refresh();
    setMessage(t('courseHoleMap.saved'));
  }, [center, draft, features, refresh, review, save, saveCoursePosition, t, turnCount]);

  const [analysing, setAnalysing] = useState(false);

  /** Queues imagery analysis for the course, from the screen where the geometry was marked. */
  const onAnalyse = async () => {
    if (analysing) return;
    setAnalysing(true);
    const outcome = await requestCourseAnalysis(apiBaseUrl, token, courseId, {
      kinds: chosenOrAll(kinds, offeredKinds),
      detectors: chosenOrAll(detectors, offeredDetectors),
      providers: chosenOrAll(providers, offeredProviders),
    });
    setAnalysing(false);
    if (outcome === 'queued') {
      // The run itself takes minutes; this only makes the modal notice a run that finished
      // while it was open, without asking the admin to close and reopen it.
      setCandidateReload((key) => key + 1);
    }
    setMessage(
      t(
        outcome === 'queued'
          ? detectors.includes('model')
            ? 'courseHoleMap.analysisQueuedModel'
            : 'courseHoleMap.analysisQueued'
          : outcome === 'unavailable'
            ? 'courseHoleMap.analysisUnavailable'
            : 'courseHoleMap.analysisFailed'
      )
    );
  };

  /**
   * Which detectors this environment can actually run. The model needs a batch worker with an
   * analyser built from the training image; where none is online the server says so and the
   * button is offered disabled rather than hidden, because "why can I not run the model here"
   * is the question an admin will have, and a missing button does not answer it.
   */
  const availableDetectors = analysisStatus?.detectors ?? [];

  /**
   * What this environment can actually be asked for right now, and what of it is chosen. The
   * offered sets come from the status (a kind whose worker is offline is not offered at all),
   * and an unset choice means all of them — so the button does today what it always did.
   */
  const offeredDetectors = useMemo(
    () => DETECTORS.filter((d) => availableDetectors.includes(d)),
    [availableDetectors]
  );
  const offeredKinds = useMemo(
    () => JOB_KINDS.filter((k) => (analysisStatus?.jobKinds ?? ['detection']).includes(k)),
    [analysisStatus?.jobKinds]
  );
  const offeredProviders = useMemo(() => analysisStatus?.providers ?? [], [analysisStatus?.providers]);
  const kinds = useMemo(
    () => (chosenKinds ?? offeredKinds).filter((k) => offeredKinds.includes(k)),
    [chosenKinds, offeredKinds]
  );
  const detectors = useMemo(
    () => (chosenDetectors ?? offeredDetectors).filter((d) => offeredDetectors.includes(d)),
    [chosenDetectors, offeredDetectors]
  );
  const providers = useMemo(
    () => (chosenProviders ?? offeredProviders).filter((p) => offeredProviders.includes(p)),
    [chosenProviders, offeredProviders]
  );
  /** Detections are the only jobs a detector or a provider narrows; hide those rows otherwise. */
  const narrowsDetections = kinds.includes('detection') || kinds.includes('imagery-products');

  /**
   * Zooms a step in or out about the middle of the viewport — where the centre cross is.
   *
   * The pan travels with the zoom, and that is the whole of it. `applyViewTransform` scales
   * about the middle of the *fitted* frame and adds the pan afterwards in screen pixels, so
   * the ground under the crosshair is `centre - pan / (scale · zoom)`: hold the pan still
   * while the zoom grows and that point creeps back towards the middle of the fitted frame.
   * Zooming in on a green two hundred metres off-centre walked away from it a step at a time,
   * which is exactly when somebody is zooming. Scaling the pan by the same factor cancels the
   * zoom out of that expression and the point under the cross stays put.
   *
   * By the factor actually applied, not the one asked for: at the ceiling the zoom does not
   * move, and a pan scaled by the requested factor would slide the map sideways in answer to
   * a button that is supposed to have done nothing.
   *
   * Zooming out is the one case this cannot honour to the end. The pan allowance shrinks with
   * the zoom — below the fit there is no room to hold an off-centre point off-centre — so
   * `clampPan` pulls the view back to the frame, which is the behaviour that has to win.
   */
  const stepZoom = (factor: number) =>
    setView((current) => {
      // The editor may go below the fit: the frame is built from the geometry being
      // corrected, so when that geometry is wrong the ground you need is outside it.
      // The editor's ceiling, not the player's: this modal is admin-only and its job is
      // placing a marker on a tee box a few metres across, which 8× the fitted frame cannot
      // resolve. The + button stopped responding well before the imagery ran out.
      const zoom = Math.min(
        MAX_EDIT_ZOOM,
        Math.max(editing ? MIN_EDIT_ZOOM : MIN_VIEW_ZOOM, current.zoom * factor)
      );
      const applied = zoom / current.zoom;
      return { zoom, panX: current.panX * applied, panY: current.panY * applied };
    });

  /**
   * Moves the frame to a typed coordinate.
   *
   * The pan and zoom are reset with it, or the new centre would be pushed straight back off
   * screen by however far the old view had been dragged — which looks exactly like the field
   * not working.
   */
  const goToTypedCoordinate = () => {
    const point = parseLatLon(gotoText);
    if (!point) {
      setGotoInvalid(true);
      return;
    }
    setGotoInvalid(false);
    setManualCenter(point);
    setView(IDENTITY_VIEW);
  };

  const goToHole = (nextIndex: number) => {
    if (dirty && editing) {
      // Nothing is discarded silently: the warning is the whole protection, since the draft
      // only lives in this component.
      setMessage(t('courseHoleMap.unsavedWarning'));
      return;
    }
    setIndex(nextIndex);
    setMessage(null);
    setSelectedShapeId(null);
  };

  /**
   * Leaves edit mode, unless that would drop work.
   *
   * The draft lives only in this component, so the warning is the whole protection — and
   * Undo is right beside the button that refuses, which is what makes refusing reasonable.
   */
  const stopEditing = () => {
    if (dirty) {
      setMessage(t('courseHoleMap.unsavedWarning'));
      return;
    }
    setEditing(false);
    setArming(null);
    setMessage(null);
    setSelectedShapeId(null);
  };

  /**
   * Closing with unsaved work warns first and closes on the second press.
   *
   * A modal that will not close is worse than one that loses a couple of clicks, so this
   * asks rather than refuses — unlike moving between holes, where staying put costs nothing.
   */
  const closeModal = () => {
    if (editing && dirty && message !== t('courseHoleMap.closeWarning')) {
      setMessage(t('courseHoleMap.closeWarning'));
      return;
    }
    setEditing(false);
    setArming(null);
    setMessage(null);
    setSelectedShapeId(null);
    onClose();
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={closeModal}>
      <View style={styles.backdrop}>
        <View style={[styles.card, editable ? styles.cardWide : null]}>
          <View style={styles.header}>
            <Pressable
              testID="course-hole-map-prev"
              accessibilityRole="button"
              // The overview is the first page — there is nothing before it. From hole 1, Prev
              // steps back out to the overview rather than being dead.
              disabled={overview}
              style={[styles.navButton, overview && styles.navButtonDisabled]}
              onPress={() => (safeIndex <= 0 ? setOverview(true) : goToHole(safeIndex - 1))}
            >
              <Text style={styles.navText}>‹</Text>
            </Pressable>

            <View style={styles.headerText}>
              <Text style={styles.title} testID="course-hole-map-title">
                {overview
                  ? t('courseHoleMap.overviewTitle')
                  : holeNumber === null
                    ? t('courseHoleMap.title')
                    : t('courseHoleMap.holeTitle', { hole: holeNumber })}
              </Text>
              {!overview && holeSubtitle ? (
                <Text style={styles.subtitle} testID="course-hole-map-subtitle">
                  {holeSubtitle}
                </Text>
              ) : null}
            </View>

            <Pressable
              testID="course-hole-map-next"
              accessibilityRole="button"
              // From the overview, Next enters the holes at whichever one the pager last sat on
              // (hole 1 on a fresh open). Within the holes it advances until the last one.
              disabled={overview ? holes.length === 0 : safeIndex >= holes.length - 1}
              style={[
                styles.navButton,
                (overview ? holes.length === 0 : safeIndex >= holes.length - 1) &&
                  styles.navButtonDisabled,
              ]}
              onPress={() => (overview ? setOverview(false) : goToHole(safeIndex + 1))}
            >
              <Text style={styles.navText}>›</Text>
            </Pressable>
          </View>

          {/* Which tee the header's length is for. Only tees this hole has a stored length for,
              so a chip is never a dead end, and the row is absent entirely on a hole nobody has
              measured from a tee — which is most of them until a card is imported. */}
          {!overview && teeLengthOptions.length > 0 ? (
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              style={styles.teeRow}
              contentContainerStyle={styles.teeRowContent}
              testID="course-hole-map-tees"
            >
              {teeLengthOptions.map((option) => {
                const active = option.teeId === selectedTeeId;
                return (
                  <Pressable
                    key={option.teeId}
                    accessibilityRole="button"
                    testID={`course-hole-map-tee-${option.teeId}`}
                    style={[styles.layerChip, !active && styles.layerChipOff]}
                    // Tapping the chosen tee again clears it, which is how the header gets back
                    // to the hole's own length without a separate "none" chip to explain.
                    onPress={() => setSelectedTeeId(active ? null : option.teeId)}
                  >
                    <Text style={styles.layerText}>
                      {t('courseHoleMap.teeChip', {
                        tee: option.teeName,
                        length: formatHoleDistance(option.lengthMeters, distanceUnit),
                      })}
                    </Text>
                  </Pressable>
                );
              })}
            </ScrollView>
          ) : null}

          {loading ? (
            <View style={styles.empty} testID="course-hole-map-loading">
              <ActivityIndicator color={appColors.accent.primary} />
            </View>
          ) : overview && holes.length > 0 ? (
            /* The complete-course overview: every hole drawn at once, each a numbered marker
               that jumps into its own page. No per-hole tools here — it is a way in, not a
               place to edit. */
            <View style={styles.body}>
              <HoleMapCanvas
                tees={NO_TEES}
                green={null}
                loading={false}
                // The overview never draws a single active hole, so there is no "this hole has
                // no green" curtain to raise — the numbered markers are the whole content.
                unavailable={false}
                showImagery={showImagery}
                storedBasemap={storedBasemap}
                fallbackCenter={center}
                fallbackSpanMeters={FALLBACK_SPAN_METERS}
                frame={overviewFrame}
                canvasStyle={canvasStyle}
                view={view}
                onPan={(pan) => setView((current) => ({ ...current, ...pan }))}
                minZoom={MIN_VIEW_ZOOM}
                otherHoles={allHoleMarkers}
                onSelectHole={(number) => {
                  const target = holes.indexOf(number);
                  if (target >= 0) {
                    setOverview(false);
                    goToHole(target);
                  }
                }}
              />
              {/* The overview is the one page whose frame covers a whole property, so it is
                  the page most in need of getting closer — picking hole 12 out of eighteen
                  markers at a fit that shows the clubhouse and the car park is guesswork.
                  Panning was already possible here; only the zoom was missing. */}
              <View style={[styles.zoomGroup, styles.overviewZoomGroup]}>
                <Pressable
                  testID="course-hole-map-overview-zoom-in"
                  accessibilityRole="button"
                  disabled={view.zoom >= MAX_EDIT_ZOOM}
                  style={styles.zoomButton}
                  onPress={() => stepZoom(ZOOM_STEP)}
                >
                  <Text style={styles.toolText}>+</Text>
                </Pressable>
                <Pressable
                  testID="course-hole-map-overview-zoom-out"
                  accessibilityRole="button"
                  disabled={view.zoom <= MIN_VIEW_ZOOM}
                  style={styles.zoomButton}
                  onPress={() => stepZoom(1 / ZOOM_STEP)}
                >
                  <Text style={styles.toolText}>−</Text>
                </Pressable>
                <Pressable
                  testID="course-hole-map-overview-zoom-fit"
                  accessibilityRole="button"
                  style={styles.zoomButton}
                  onPress={() => setView(IDENTITY_VIEW)}
                >
                  <Text style={styles.toolText}>⤢</Text>
                </Pressable>
              </View>
              <Text style={styles.counter} testID="course-hole-map-overview-counter">
                {t('courseHoleMap.overviewCounter', { total: holes.length })}
              </Text>
            </View>
          ) : holeNumber === null ? (
            <View style={styles.empty} testID="course-hole-map-empty">
              <Text style={styles.emptyText}>{t('courseHoleMap.empty')}</Text>
            </View>
          ) : (
            /* The card scrolls as one, rather than the reading being a scrolling box inside a
               fixed one. With both the map and the list wanting a height, a card capped at
               90% of the window has to take it from somewhere, and every way of dividing it
               starves one of them on some screen — the list collapsed to a single line on a
               laptop, and giving way instead left the map too small to click accurately. */
            <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
              <HoleMapCanvas
                tees={shown.tees}
                green={shown.green}
                loading={false}
                polygons={polygons}
                polylines={polylines}
                // While editing this is the draft's own line — back tee, its turning points,
                // green — so the bends are draggable and the line follows them. Not the stored
                // one, which would sit across the imagery contradicting the markers being
                // moved on top of it until the save lands.
                centreline={editing || hasPlacedTurns ? drawnPath : playingLine}
                showImagery={showImagery}
                storedBasemap={storedBasemap}
                // In edit mode an empty hole is the job, not a failure, so the "no geometry"
                // curtain would only be in the way of the map being clicked. Nor is it a
                // failure when the hole has boundaries but no green: there is a picture to
                // look at, and covering it says the opposite.
                unavailable={!editing && shown.green === null && holeShapes.length === 0}
                // The nearest mapped hole's own position beats the club's coordinate as a
                // starting guess — see `predictedCenter` — and only when no hole on the course
                // has been mapped at all does this fall back to the club's, over a wider span
                // since that could be anywhere on the property.
                fallbackCenter={manualCenter ?? predictedCenter?.position ?? center}
                fallbackSpanMeters={
                  manualCenter || !predictedCenter || predictedCenter.step > 1
                    ? FALLBACK_SPAN_METERS
                    : PREDICTED_SPAN_METERS
                }
                // While editing the view stays put as markers go down, so placing a green
                // 300 m from the tee does not mean hunting for it after every click.
                holdFallbackFrame={editing}
                // The frame is fit to the saved geometry, not the live draft — see the prop's
                // own doc. Without this, placing a tee or green recentres the map on the spot
                // just clicked, which reads as the map moving out from under the click.
                frame={frame}
                // A confirmed hole fills the frame instead of sharing it with the neighbours —
                // an unmapped hole keeps the wide defaults, which is what makes it double as a
                // course overview (see `holeIsMapped`'s own doc).
                horizontalPadFraction={holeIsMapped ? MAPPED_HORIZONTAL_PAD : undefined}
                verticalPadFraction={holeIsMapped ? MAPPED_VERTICAL_PAD : undefined}
                view={view}
                canvasStyle={canvasStyle}
                aiming={arming !== null}
                // Always wired, not just while editing: `onMapPress` itself branches on
                // `editing`/`arming` to decide between placing a marker and inspecting
                // whatever shape the tap landed on — the read-only map wants the second one.
                onMapPress={onMapPress}
                // Only while something is armed: the line answers "what would this click do",
                // and there is no click coming when nothing is waiting to be placed. Web only
                // inside the canvas, so a phone simply never reports one.
                onHoverPoint={editing && arming ? setHoverPoint : undefined}
                measureLine={measure}
                // Panning is not an editing tool, and gating it on edit mode made the
                // read-only map a picture you could zoom into and then not move around in:
                // the +/- buttons drive the view directly and kept working, so the map
                // responded to every control except dragging it. A drag under the threshold
                // still resolves as a tap, and the hole markers are Pressables, so tapping a
                // neighbouring hole to jump to it is unaffected.
                onPan={(pan) => setView((current) => ({ ...current, ...pan }))}
                // Only while editing: dragging a marker is editing it, and on the read-only
                // map a drag must stay a pan.
                onMarkerDrag={editing ? onMarkerDrag : undefined}
                // Only while editing: it is a tool for checking that the frame is over the
                // right ground, which is a question the person moving markers is asking.
                showCenterCoordinate={editing}
                minZoom={editing ? MIN_EDIT_ZOOM : MIN_VIEW_ZOOM}
                maxZoom={MAX_EDIT_ZOOM}
                otherHoles={otherHoles}
                // Numbered like its neighbours, so the hole being looked at is not the only
                // unlabelled line on the map. Not while editing, and that is the same `editing`
                // as `showCenterCoordinate` above on purpose: the badge sits at the middle of
                // the tee-to-green chord, which on a frame fitted to this one hole is about
                // where the centre cross lands. Tying both to the same flag keeps the disc off
                // the crosshair rather than leaving them to overlap.
                currentHole={editing ? null : currentHoleMarker}
                // Left out while editing: a tap near a marker should never compete with placing
                // or panning.
                onSelectHole={
                  editing
                    ? undefined
                    : (number) => {
                        const target = holes.indexOf(number);
                        if (target >= 0) goToHole(target);
                      }
                }
              />

              {/* What a tap on the map landed on — the same row content the list below
                  already gives every shape, surfaced here so finding out which of however
                  many bunkers this hole has does not mean scrolling to hunt for it. */}
              {selectedShape ? (
                <View style={styles.shapeInfoCard} testID="course-hole-map-shape-info">
                  <View style={styles.shapeInfoHeading}>
                    <View style={styles.shapeHeading}>
                      <View
                        style={[
                          styles.layerSwatch,
                          { backgroundColor: OVERLAY_COLORS[selectedShape.kind] ?? appColors.text.onMedia },
                        ]}
                      />
                      <Text style={styles.rowLabel}>{kindLabel(selectedShape.kind)}</Text>
                    </View>
                    <Pressable
                      testID="course-hole-map-shape-info-close"
                      accessibilityRole="button"
                      onPress={() => setSelectedShapeId(null)}
                    >
                      <Text style={styles.toolText}>{t('courseHoleMap.close')}</Text>
                    </Pressable>
                  </View>
                  <Text style={styles.rowCoords}>{describeShape(selectedShape)}</Text>
                  <Text style={styles.rowMeta}>
                    {t('courseHoleMap.meta', {
                      source: sourceLabel(selectedShape.source, selectedShape.sourceProvider),
                      confidence: Math.round(selectedShape.confidence * 100),
                      contributors: selectedShape.sampleCount,
                    })}
                  </Text>
                  {servingNote(selectedShape, t) ? (
                    <Text style={styles.rowMeta} testID="course-hole-map-shape-info-serving">
                      {servingNote(selectedShape, t)}
                    </Text>
                  ) : null}
                </View>
              ) : null}

              {/* Offered in edit mode too. Marking a green is aiming at a shape the analyser
                  has often already outlined, and being able to see that outline — or to take
                  the imagery away and check the outline is not nonsense — is the difference
                  between placing a point and guessing at one. */}
              {layers.length > 0 ? (
                <View style={styles.layerBar} testID="course-hole-map-layers">
                  {layers.map((layer) => {
                    const hidden = hiddenKinds.includes(layer.kind);
                    return (
                      <Pressable
                        key={layer.kind}
                        testID={`course-hole-map-layer-${layer.kind}`}
                        accessibilityRole="button"
                        aria-pressed={!hidden}
                        style={[styles.layerChip, hidden ? styles.layerChipOff : null]}
                        onPress={() => setHiddenKinds((current) => toggleKind(current, layer.kind))}
                      >
                        {/* The swatch is the legend: it is the only thing tying the row to
                            the colour on the map, and it survives the label being long. */}
                        <View
                          style={[
                            styles.layerSwatch,
                            { backgroundColor: OVERLAY_COLORS[layer.kind] ?? appColors.text.onMedia },
                            hidden ? styles.layerSwatchOff : null,
                          ]}
                        />
                        <Text style={styles.layerText}>
                          {kindLabel(layer.kind)} {layer.count}
                        </Text>
                      </Pressable>
                    );
                  })}

                  <View style={[styles.basemapGroup, styles.imageryChip]}>
                    {BASEMAPS.map((option) => {
                      const active = basemap === option;
                      return (
                        <Pressable
                          key={option}
                          testID={`course-hole-map-basemap-${option}`}
                          accessibilityRole="button"
                          aria-pressed={active}
                          style={[styles.layerChip, active ? null : styles.layerChipOff]}
                          onPress={() => {
                            basemapPicked.current = true;
                            setBasemap(option);
                          }}
                        >
                          <Text style={styles.layerText}>{t(`courseHoleMap.basemap_${option}`)}</Text>
                        </Pressable>
                      );
                    })}
                  </View>
                </View>
              ) : null}

              {/* Independent of the basemap picture above: this decides which shape sources
                  are drawn, and — unlike the basemap — more than one can be on at once, so a
                  provider's satellite detections and OSM's own import can be compared side
                  by side. Gated on the hole's raw shapes, not the filtered `layers`, so
                  switching every source off does not also hide the way to switch one back
                  on. */}
              {holeShapes.length > 0 ? (
                <View style={styles.layerBar} testID="course-hole-map-sources">
                  {SOURCE_OPTIONS.map((option) => {
                    const active = sourceFilter.includes(option);
                    return (
                      <Pressable
                        key={option}
                        testID={`course-hole-map-source-${option}`}
                        accessibilityRole="button"
                        aria-pressed={active}
                        style={[styles.layerChip, active ? null : styles.layerChipOff]}
                        onPress={() =>
                          setSourceFilter((current) =>
                            current.includes(option)
                              ? current.filter((selected) => selected !== option)
                              : [...current, option]
                          )
                        }
                      >
                        <Text style={styles.layerText}>{t(`courseHoleMap.source_${option}`)}</Text>
                      </Pressable>
                    );
                  })}

                  {/* What a run returned, as opposed to what the course publishes. Kept in the
                      same row as the published sources on purpose: the comparison is the
                      point, and two rows of switches would read as two unrelated pictures. */}
                  {candidateOptions.map(({ option, detector: runDetector, model, count }) => {
                    const active = sourceFilter.includes(option);
                    return (
                      <Pressable
                        key={option}
                        testID={`course-hole-map-source-${option}`}
                        accessibilityRole="button"
                        aria-pressed={active}
                        style={[
                          styles.layerChip,
                          styles.candidateChip,
                          active ? null : styles.layerChipOff,
                        ]}
                        onPress={() =>
                          setSourceFilter((current) =>
                            current.includes(option)
                              ? current.filter((selected) => selected !== option)
                              : [...current, option]
                          )
                        }
                      >
                        <Text style={styles.layerText}>
                          {t(`courseHoleMap.candidates_${runDetector}`)}
                          {model ? ` ${model}` : ''} {count}
                        </Text>
                      </Pressable>
                    );
                  })}
                  {/* The runs' polygons are a course-sized read and arrive seconds after the
                      published rows. Until they do, the row would say the model has never run
                      here, and an admin who queued that run looks for its switch and finds
                      nothing. */}
                  {candidatesLoading && candidateOptions.length === 0 ? (
                    <View
                      testID="course-hole-map-candidates-loading"
                      style={[styles.layerChip, styles.candidateChip, styles.layerChipOff]}
                    >
                      <Text style={styles.layerText}>{t('courseHoleMap.candidatesLoading')}</Text>
                    </View>
                  ) : null}

                  {/* What an evaluation scored, verdict by verdict. Same row again: the model's
                      evaluation next to the classical detector's next to OSM is the comparison. */}
                  {evaluationOptions.map(({ option, detector: runDetector, model, matched, missed, falsePositive }) => {
                    const active = sourceFilter.includes(option);
                    return (
                      <Pressable
                        key={option}
                        testID={`course-hole-map-source-${option}`}
                        accessibilityRole="button"
                        aria-pressed={active}
                        style={[styles.layerChip, styles.candidateChip, active ? null : styles.layerChipOff]}
                        onPress={() =>
                          setSourceFilter((current) =>
                            current.includes(option)
                              ? current.filter((selected) => selected !== option)
                              : [...current, option]
                          )
                        }
                      >
                        <Text style={styles.layerText}>
                          {t(`courseHoleMap.evaluation_${runDetector}`)}
                          {model ? ` ${model}` : ''}{' '}
                          {t('courseHoleMap.evaluationCounts', { matched, falsePositive, missed })}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>
              ) : null}

              {editable && !analysisStatusLoading && !availableDetectors.includes('model') ? (
                <Text style={styles.hint} testID="course-hole-map-detector-unavailable">
                  {t('courseHoleMap.detectorModelUnavailable')}
                </Text>
              ) : null}

              {isNationalOrtho(basemap) && !storedBasemap && !analysisStatusLoading ? (
                <Text style={styles.hint} testID="course-hole-map-basemap-unavailable">
                  {t('courseHoleMap.basemapUnavailable')}
                </Text>
              ) : null}

              {editable ? (
                <View style={styles.editBar}>
                  <Pressable
                    testID="course-hole-map-edit-toggle"
                    accessibilityRole="button"
                    style={[styles.toolButton, editing ? styles.toolButtonActive : null]}
                    onPress={() => {
                      if (editing) {
                        stopEditing();
                        return;
                      }
                      setEditing(true);
                      setArming(null);
                      setMessage(null);
                    }}
                  >
                    <Text style={styles.toolText}>
                      {t(editing ? 'courseHoleMap.editDone' : 'courseHoleMap.edit')}
                    </Text>
                  </Pressable>

                  {editing ? (
                    <>
                      <Pressable
                        testID="course-hole-map-place-tee"
                        accessibilityRole="button"
                        style={[styles.toolButton, arming === 'tee' ? styles.toolButtonActive : null]}
                        onPress={() => {
                          setArming(arming === 'tee' ? null : 'tee');
                          setHoverPoint(null);
                        }}
                      >
                        <Text style={styles.toolText}>{t('courseHoleMap.placeTee')}</Text>
                      </Pressable>
                      <Pressable
                        testID="course-hole-map-place-green"
                        accessibilityRole="button"
                        style={[
                          styles.toolButton,
                          arming === 'green' ? styles.toolButtonActive : null,
                        ]}
                        onPress={() => {
                          setArming(arming === 'green' ? null : 'green');
                          setHoverPoint(null);
                        }}
                      >
                        <Text style={styles.toolText}>{t('courseHoleMap.placeGreen')}</Text>
                      </Pressable>
                      <Pressable
                        testID="course-hole-map-revert"
                        accessibilityRole="button"
                        disabled={!dirty || saving}
                        style={[styles.toolButton, !dirty || saving ? styles.toolButtonDisabled : null]}
                        onPress={() => {
                          setDraft(draftFromFeatures(holeNumber, features));
                          setArming(null);
                          setMessage(null);
                        }}
                      >
                        <Text style={styles.toolText}>{t('courseHoleMap.revert')}</Text>
                      </Pressable>
                      <Pressable
                        testID="course-hole-map-save"
                        accessibilityRole="button"
                        disabled={!dirty || saving}
                        style={[
                          styles.toolButton,
                          styles.saveButton,
                          !dirty || saving ? styles.toolButtonDisabled : null,
                        ]}
                        onPress={() => void onSave()}
                      >
                        <Text style={styles.toolText}>
                          {saving ? t('courseHoleMap.saving') : t('courseHoleMap.save')}
                        </Text>
                      </Pressable>
                    </>
                  ) : null}

                  {/* Outside the `editing` block on purpose: re-running the analyser is not
                      only useful right after marking a tee and green — a hole that is already
                      mapped may need a rerun too (fresher imagery, a previous run that came
                      back thin), and that should not require opening the editor first. Still
                      disabled on `dirty`, which stays false outside editing anyway, since the
                      analyser reads whatever is saved, not whatever is still in the draft. */}
                  <Pressable
                    testID="course-hole-map-analyse"
                    accessibilityRole="button"
                    disabled={analysing || dirty}
                    style={[styles.toolButton, analysing || dirty ? styles.toolButtonDisabled : null]}
                    onPress={() => void onAnalyse()}
                  >
                    <Text style={styles.toolText}>
                      {analysing ? t('courseHoleMap.analysing') : t('courseHoleMap.analyse')}
                    </Text>
                  </Pressable>

                  {/* What that button asks for. Beside it rather than in a menu because it is
                      the reason to press it twice: the same hole, the same imagery, the
                      classical thresholds and then the trained model, drawn over each other by
                      the chips above — and because re-cutting one provider's pictures should
                      not cost two twenty-minute model runs. */}
                  <View style={styles.basemapGroup} testID="course-hole-map-job-kinds">
                    {offeredKinds.map((option) => (
                      <Pressable
                        key={option}
                        testID={`course-hole-map-kind-${option}`}
                        accessibilityRole="button"
                        aria-pressed={kinds.includes(option)}
                        style={[styles.layerChip, kinds.includes(option) ? null : styles.layerChipOff]}
                        onPress={() => setChosenKinds(toggled(kinds, option))}
                      >
                        <Text style={styles.layerText}>{t(`courseAnalysis.status.jobKind.${option}`)}</Text>
                      </Pressable>
                    ))}
                  </View>

                  {narrowsDetections ? (
                    <View style={styles.basemapGroup} testID="course-hole-map-detectors">
                      {DETECTORS.map((option) => {
                        const unavailable = !availableDetectors.includes(option);
                        return (
                          <Pressable
                            key={option}
                            testID={`course-hole-map-detector-${option}`}
                            accessibilityRole="button"
                            aria-pressed={detectors.includes(option)}
                            aria-disabled={unavailable}
                            disabled={unavailable}
                            style={[
                              styles.layerChip,
                              detectors.includes(option) ? null : styles.layerChipOff,
                              unavailable ? styles.toolButtonDisabled : null,
                            ]}
                            onPress={() => setChosenDetectors(toggled(detectors, option))}
                          >
                            <Text style={styles.layerText}>{t(`courseHoleMap.detector_${option}`)}</Text>
                          </Pressable>
                        );
                      })}
                    </View>
                  ) : null}

                  {narrowsDetections && offeredProviders.length > 1 ? (
                    <View style={styles.basemapGroup} testID="course-hole-map-analysis-providers">
                      {offeredProviders.map((option) => (
                        <Pressable
                          key={option}
                          testID={`course-hole-map-analysis-provider-${option}`}
                          accessibilityRole="button"
                          aria-pressed={providers.includes(option)}
                          style={[styles.layerChip, providers.includes(option) ? null : styles.layerChipOff]}
                          onPress={() => setChosenProviders(toggled(providers, option))}
                        >
                          <Text style={styles.layerText}>{sourceLabel(option, null)}</Text>
                        </Pressable>
                      ))}
                    </View>
                  ) : null}

                  <View style={styles.zoomGroup}>
                    <Pressable
                      testID="course-hole-map-zoom-in"
                      accessibilityRole="button"
                      disabled={view.zoom >= MAX_EDIT_ZOOM}
                      style={styles.zoomButton}
                      onPress={() => stepZoom(ZOOM_STEP)}
                    >
                      <Text style={styles.toolText}>+</Text>
                    </Pressable>
                    <Pressable
                      testID="course-hole-map-zoom-out"
                      accessibilityRole="button"
                      disabled={view.zoom <= (editing ? MIN_EDIT_ZOOM : MIN_VIEW_ZOOM)}
                      style={styles.zoomButton}
                      onPress={() => stepZoom(1 / ZOOM_STEP)}
                    >
                      <Text style={styles.toolText}>−</Text>
                    </Pressable>
                    <Pressable
                      testID="course-hole-map-zoom-fit"
                      accessibilityRole="button"
                      style={styles.zoomButton}
                      onPress={() => setView(IDENTITY_VIEW)}
                    >
                      <Text style={styles.toolText}>⤢</Text>
                    </Pressable>
                  </View>
                </View>
              ) : null}

              {/* Typing a coordinate is the way back when the frame is over the wrong ground
                  entirely. Panning only helps once you are near; a course whose stored
                  position is wrong can be kilometres out, and the imagery gives nothing to
                  navigate by. Paste the club's real coordinate and the map goes there. */}
              {editing ? (
                <View style={styles.gotoRow}>
                  <TextInput
                    testID="course-hole-map-goto-input"
                    style={styles.gotoInput}
                    value={gotoText}
                    onChangeText={(text) => {
                      setGotoText(text);
                      setGotoInvalid(false);
                    }}
                    onSubmitEditing={goToTypedCoordinate}
                    placeholder={t('courseHoleMap.gotoPlaceholder')}
                    placeholderTextColor={appColors.text.muted}
                    autoCapitalize="none"
                    autoCorrect={false}
                    inputMode="text"
                    returnKeyType="go"
                  />
                  <Pressable
                    testID="course-hole-map-goto"
                    accessibilityRole="button"
                    disabled={gotoText.trim().length === 0}
                    style={[
                      styles.toolButton,
                      gotoText.trim().length === 0 ? styles.toolButtonDisabled : null,
                    ]}
                    onPress={goToTypedCoordinate}
                  >
                    <Text style={styles.toolText}>{t('courseHoleMap.goto')}</Text>
                  </Pressable>
                </View>
              ) : null}

              {gotoInvalid ? (
                <Text style={styles.gotoError} testID="course-hole-map-goto-invalid">
                  {t('courseHoleMap.gotoInvalid')}
                </Text>
              ) : null}

              {editing ? (
                <Text style={styles.hint} testID="course-hole-map-hint">
                  {t(arming === null ? 'courseHoleMap.editHint' : 'courseHoleMap.armedHint')}
                </Text>
              ) : null}

              {/* Only where the hole has bends to drag — a par 3 has none, and an unknown par
                  says nothing about how the hole is played, so neither gets an instruction for
                  points that are not on the map. */}
              {editing && turnCount > 0 ? (
                <Text style={styles.hint} testID="course-hole-map-turn-hint">
                  {t('courseHoleMap.turnHint')}
                </Text>
              ) : null}

              {message ? (
                <Text style={styles.message} testID="course-hole-map-message">
                  {message}
                </Text>
              ) : null}

              {/* Every length this hole is stored with, source by source — admin-only, because
                  it is the disagreement between sources that is worth showing and a player is
                  served one number rather than asked to judge between five. */}
              {editable ? <HoleLengthTable hole={holeCard} distanceUnit={distanceUnit} /> : null}

              {/* A plain view, not a scroller: the card is the scroller now, and a scrolling
                  box inside a scrolling box is a trap for the wheel. */}
              <View>
                {editing && draft
                  ? draft.markers.map((marker) => (
                      <View
                        key={markerKey(marker)}
                        style={styles.editRow}
                        testID={`course-hole-map-draft-${markerKey(marker)}`}
                      >
                        <View style={styles.editRowText}>
                          <Text style={styles.rowLabel}>
                            {pointLabel(marker.kind, marker.ordinal)}
                            {marker.name ? ` ${marker.name}` : ''}
                          </Text>
                          <Text style={styles.rowCoords} selectable>
                            {marker.lat.toFixed(6)}, {marker.lon.toFixed(6)}
                          </Text>
                        </View>
                        <Pressable
                          testID={`course-hole-map-remove-${markerKey(marker)}`}
                          accessibilityRole="button"
                          style={styles.removeButton}
                          onPress={() => setDraft(removeMarker(draft, markerKey(marker)))}
                        >
                          <Text style={styles.toolText}>{t('courseHoleMap.remove')}</Text>
                        </Pressable>
                      </View>
                    ))
                  : holeFeatures.map((feature) => (
                      <View key={feature.id} style={styles.row} testID={`course-hole-map-row-${feature.id}`}>
                        <Text style={styles.rowLabel}>{describe(feature)}</Text>
                        {/* Six decimals is ~0.1m — finer than anything upstream, and the precision
                            an admin needs to paste a coordinate somewhere else and compare. */}
                        <Text style={styles.rowCoords} selectable>
                          {feature.lat.toFixed(6)}, {feature.lon.toFixed(6)}
                        </Text>
                        <Text style={styles.rowMeta} testID={`course-hole-map-row-meta-${feature.id}`}>
                          {t('courseHoleMap.meta', {
                            source: sourceLabel(feature.source, feature.sourceProvider),
                            confidence: Math.round(feature.confidence * 100),
                            contributors: feature.sampleCount,
                          })}
                        </Text>
                        {servingNote(feature, t) ? (
                          <Text style={styles.rowMeta} testID={`course-hole-map-row-serving-${feature.id}`}>
                            {servingNote(feature, t)}
                          </Text>
                        ) : null}
                      </View>
                    ))}

                {/* Below the points in both modes, including while editing: a boundary is not
                    editable here, so it is context for the marker being placed rather than
                    another row to act on. Filtered with the map, so switching a kind off
                    takes it out of the reading as well as out of the picture. */}
                {shownShapes.map((shape) => (
                  <View
                    key={shape.id}
                    style={styles.row}
                    testID={`course-hole-map-shape-${shape.id}`}
                  >
                    <View style={styles.shapeHeading}>
                      <View
                        style={[
                          styles.layerSwatch,
                          { backgroundColor: OVERLAY_COLORS[shape.kind] ?? appColors.text.onMedia },
                        ]}
                      />
                      <Text style={styles.rowLabel}>{kindLabel(shape.kind)}</Text>
                    </View>
                    <Text style={styles.rowCoords}>{describeShape(shape)}</Text>
                    <Text style={styles.rowMeta} testID={`course-hole-map-shape-meta-${shape.id}`}>
                      {t('courseHoleMap.meta', {
                        source: sourceLabel(shape.source, shape.sourceProvider),
                        confidence: Math.round(shape.confidence * 100),
                        contributors: shape.sampleCount,
                      })}
                    </Text>
                    {servingNote(shape, t) ? (
                      <Text style={styles.rowMeta} testID={`course-hole-map-shape-serving-${shape.id}`}>
                        {servingNote(shape, t)}
                      </Text>
                    ) : null}
                  </View>
                ))}
              </View>

              <Text style={styles.counter}>
                {t('courseHoleMap.counter', { index: safeIndex + 1, total: holes.length })}
              </Text>
            </ScrollView>
          )}

          <Pressable
            accessibilityRole="button"
            testID="course-hole-map-close"
            style={styles.closeButton}
            onPress={closeModal}
          >
            <Text style={styles.closeText}>{t('menu.close')}</Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}

/**
 * What the row's numbers mean for a player: how many providers agree it is there, and — for
 * a satellite suggestion under the confidence floor — that nobody but an admin sees it yet.
 * Null when there is nothing to add to the meta line.
 */
const servingNote = (
  row: { agreementCount?: number; served?: boolean },
  t: (key: string, params?: Record<string, string | number>) => string
): string | null => {
  const parts = [
    (row.agreementCount ?? 0) > 1 ? t('courseHoleMap.agreement', { count: row.agreementCount ?? 0 }) : null,
    // Only a row that is published and withheld says so; a candidate carries no serving status.
    row.served === false ? t('courseHoleMap.notServed') : null,
  ].filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join(' · ') : null;
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: alpha(appColors.neutral.black, 0.6),
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  card: {
    width: '100%',
    maxWidth: 460,
    maxHeight: '90%',
    borderRadius: 12,
    padding: 14,
    gap: 10,
    backgroundColor: appColors.background.panel,
  },
  // Marking a tee box means clicking a shape a few metres across. At 460px that is a
  // handful of pixels; on a laptop there is no reason to make someone aim at it.
  cardWide: Platform.select({ web: { maxWidth: 900 }, default: {} }) as object,
  header: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  headerText: { flex: 1, alignItems: 'center' },
  title: { color: appColors.text.primary, fontSize: 18, fontWeight: '700' },
  subtitle: { color: appColors.text.muted, fontSize: 13, marginTop: 2 },
  navButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: appColors.border.subtle,
  },
  navButtonDisabled: { opacity: 0.3 },
  navText: { color: appColors.text.primary, fontSize: 22, lineHeight: 26 },
  empty: { paddingVertical: 40, alignItems: 'center' },
  emptyText: { color: appColors.text.primary, fontSize: 14, textAlign: 'center' },
  editBar: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 8 },
  toolButton: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.border.subtle,
  },
  toolButtonActive: { borderColor: appColors.border.accent, backgroundColor: alpha(appColors.accent.primary, 0.18) },
  toolButtonDisabled: { opacity: 0.4 },
  saveButton: { borderColor: appColors.border.accent },
  zoomGroup: { flexDirection: 'row', gap: 6, marginLeft: 'auto' },
  // The overview body has no gap of its own — its two children used to be the map and the
  // hole count, which sit fine against each other; a row of buttons does not.
  overviewZoomGroup: { marginTop: 8, marginBottom: 8 },
  gotoRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  gotoInput: {
    flex: 1,
    minWidth: 140,
    height: 36,
    paddingHorizontal: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.surface.base,
    color: appColors.text.primary,
    fontSize: 13,
    fontVariant: ['tabular-nums'],
  },
  gotoError: { color: appColors.status.warning, fontSize: 12, lineHeight: 16 },
  zoomButton: {
    width: 34,
    height: 34,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: appColors.border.subtle,
  },
  toolText: { color: appColors.text.primary, fontSize: 14, fontWeight: '600' },
  hint: { color: appColors.text.muted, fontSize: 12 },
  message: { color: appColors.text.primary, fontSize: 13 },
  layerBar: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 6 },
  layerChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 4,
    paddingHorizontal: 9,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: appColors.border.subtle,
  },
  // Switched off reads as faded rather than as a different control: the chip still has to say
  // what it would bring back.
  layerChipOff: { opacity: 0.4 },
  imageryChip: { marginLeft: 'auto' },
  // Its own row under the title rather than inside it: the header is a row with a nav button
  // at each end, and a course with five tees would have nowhere to put them.
  teeRow: { flexGrow: 0, marginTop: 8 },
  teeRowContent: { flexDirection: 'row', gap: 6, paddingHorizontal: 2 },
  basemapGroup: { flexDirection: 'row', gap: 6 },
  // Dashed, so a candidate never reads as a published boundary at a glance — it is a guess
  // nobody has accepted, and on this screen that difference is the whole subject.
  candidateChip: { borderStyle: 'dashed' },
  layerSwatch: { width: 10, height: 10, borderRadius: 2 },
  layerSwatchOff: { opacity: 0.5 },
  layerText: { color: appColors.text.primary, fontSize: 12, fontWeight: '600' },
  body: { flexShrink: 1 },
  // The gap the card used to provide for these children, now that they are one scroller's
  // content rather than the card's own children.
  bodyContent: { gap: 10 },
  row: { paddingVertical: 6 },
  shapeHeading: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  shapeInfoCard: {
    borderWidth: 1,
    borderColor: appColors.border.subtle,
    borderRadius: 10,
    padding: 10,
    gap: 2,
  },
  shapeInfoHeading: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  editRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    paddingVertical: 6,
  },
  editRowText: { flexShrink: 1 },
  removeButton: {
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.border.subtle,
  },
  rowLabel: { color: appColors.text.primary, fontSize: 14, fontWeight: '600' },
  rowCoords: { color: appColors.text.secondary, fontSize: 13, marginTop: 1 },
  rowMeta: { color: appColors.text.muted, fontSize: 11, marginTop: 1 },
  counter: { color: appColors.text.muted, fontSize: 12, textAlign: 'center' },
  closeButton: {
    alignSelf: 'center',
    paddingVertical: 10,
    paddingHorizontal: 24,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.accent,
  },
  closeText: { color: appColors.text.primary, fontSize: 15, fontWeight: '700' },
});
